import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Set up axios default headers when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('token', token);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
    }
  }, [token]);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          const response = await axios.get(`${API_URL}/api/auth/me`);
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          // Token is invalid or expired — signal Login page to show a toast
          try { sessionStorage.setItem('auth_session_expired', '1'); } catch { /* ignore */ }
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password
    });
    
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      email,
      password,
      name,
      role: 'admin'
    });
    
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateUser = async (data) => {
    const response = await axios.put(`${API_URL}/api/auth/me`, data);
    setUser(response.data);
    return response.data;
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    updateUser
  };

  // Global 401 handler: if any API request returns 401 (usually expired JWT),
  // clear the stale session and bounce the user to /login. Without this, the
  // page just shows "could not validate credentials" toasts forever until the
  // user manually signs out — see incident on Feb 25, 2026 (BEO create fail).
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;
        // Only auto-logout for real token failures — NOT for /login endpoint
        // (bad password) or for missing-permission 403s that come from role checks.
        const url = error?.config?.url || '';
        const isLoginCall = url.endsWith('/api/auth/login') || url.endsWith('/api/auth/register');
        const isTokenExpired = status === 401 && !isLoginCall && (
          detail === 'Could not validate credentials' ||
          detail === 'Token expired' ||
          detail === 'Invalid token'
        );
        if (isTokenExpired && localStorage.getItem('token')) {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setToken(null);
          setUser(null);
          try {
            sessionStorage.setItem('auth_session_expired', '1');
            if (window.location.pathname !== '/login' && !window.location.pathname.startsWith('/client-portal')) {
              sessionStorage.setItem('post_login_redirect', window.location.pathname + window.location.search);
            }
          } catch { /* ignore */ }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
